import React, { useEffect } from 'react';

import { AddOutlined, Remove } from '@mui/icons-material';
import { Autocomplete, IconButton } from '@mui/material';
import AppCheckbox from 'components/FormElements/AppCheckBox';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import AppTextInput from 'components/FormElements/AppTextInput';
import FieldRow from 'components/FormElements/FieldRow';
import { Row } from 'components/Layout';
import PageSection from 'components/PageSection';
import Spacing from 'components/Spacing';
import { AccountType } from 'further-types/investor';
import { nationality } from 'further-ui/labels';
import { InvestorFormValues } from 'helpers/investor/investorForm';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import TextField from './InvestorDetails/TextField';
import { IncomeSource } from 'further-types/investor';
import { IncomeSourceLabels } from 'further-ui/labels';

type Props = {
  control: Control<InvestorFormValues>;
};

const TaxAndBankDetail: React.FC<Props> = ({ control }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'taxResidencies',
  });

  const accountType = useWatch({
    control,
    name: 'accountType',
  });

  const {
    fields: incomeSourceFields,
    append: appendIncomeSource,
    remove: removeIncomeSource,
  } = useFieldArray({
    control,
    name: 'incomeSources',
  });

  // Ensure there's at least one income source row for individual investors:
  useEffect(() => {
    if (accountType !== AccountType.Individual) return;
    if (incomeSourceFields.length) return;
    appendIncomeSource({ value: '' });
  }, [accountType, appendIncomeSource, incomeSourceFields.length]);

  const taxResidenciesLabel =
    accountType === AccountType.LegalEntity
      ? 'Entity tax domicile/residency'
      : 'Tax residency(ies)';

  return (
    <PageSection title="Tax and bank details">
      <Spacing>
        <FieldRow title="Bank account name" centerTitle>
          <TextField name="bankAccountName" control={control} />
        </FieldRow>
        <FieldRow title="Bank name" centerTitle>
          <TextField name="bankName" control={control} />
        </FieldRow>
        <FieldRow title="Bank sort code" centerTitle>
          <Controller
            name="bankSortCode"
            control={control}
            render={({
              field: { ref, ...rest },
              fieldState: { error: bankSortCodeError },
            }) => (
              <PatternFormat
                placeholder="Enter bank sort code"
                format="##-##-##"
                customInput={AppTextInput}
                type="text"
                {...rest}
                inputRef={ref}
                error={!!bankSortCodeError}
                helperText={bankSortCodeError?.message}
              />
            )}
          />
        </FieldRow>
        <FieldRow title="Bank account number" centerTitle>
          <TextField name="bankAccountNumber" control={control} />
        </FieldRow>
        <FieldRow
          title="IBAN or non-UK bank account number (if applicable)"
          centerTitle
        >
          <TextField name="iban" control={control} />
        </FieldRow>
        <FieldRow title="SWIFT/BIC (if applicable)" centerTitle>
          <TextField name="swift" control={control} />
        </FieldRow>

        {accountType === AccountType.Individual && (
          <FieldRow title="Select source of funds" centerTitle>
            {incomeSourceFields.map((incomeSource, i) => (
              <React.Fragment key={incomeSource.id}>
                <Row centered spacing="xs">
                  <Controller
                    name={`incomeSources.${i}.value`}
                    control={control}
                    render={({
                      field: { ref, ...rest },
                      fieldState: { error },
                    }) => (
                      <>
                        <Autocomplete
                          freeSolo
                          fullWidth
                          options={Object.values(IncomeSource).map(
                            (source) => ({
                              id: source,
                              title: IncomeSourceLabels[source] ?? source,
                            }),
                          )}
                          getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.title
                          }
                          value={
                            Object.values(IncomeSource)
                              .map((source) => ({
                                id: source,
                                title: IncomeSourceLabels[source] ?? source,
                              }))
                              .find((opt) => opt.id === rest.value) ||
                            rest.value ||
                            ''
                          }
                          onChange={(_event, newValue) => {
                            if (typeof newValue === 'string') {
                              rest.onChange(newValue);
                            } else if (
                              newValue &&
                              typeof newValue === 'object'
                            ) {
                              rest.onChange(newValue.id);
                            } else {
                              rest.onChange('');
                            }
                          }}
                          onInputChange={(_event, newInputValue, reason) => {
                            if (
                              reason === 'input' &&
                              newInputValue !== rest.value
                            ) {
                              rest.onChange(newInputValue);
                            }
                          }}
                          renderInput={(params) => (
                            <AppTextInput
                              {...params}
                              variant="outlined"
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                        <IconButton
                          onClick={() => appendIncomeSource({ value: '' })}
                          size="large"
                        >
                          <AddOutlined />
                        </IconButton>
                        <IconButton
                          disabled={incomeSourceFields.length === 1}
                          onClick={() =>
                            incomeSourceFields.length > 1
                              ? removeIncomeSource(i)
                              : null
                          }
                          size="large"
                        >
                          <Remove />
                        </IconButton>
                      </>
                    )}
                  />
                </Row>
              </React.Fragment>
            ))}
          </FieldRow>
        )}

        <FieldRow
          title="ABA/ACH/International code (if applicable)"
          centerTitle
        >
          <TextField name="internationalBankCode" control={control} />
        </FieldRow>
        {accountType === AccountType.Individual ? (
          <>
            <FieldRow title="Primary tax ID (e.g. NI number)" centerTitle>
              <TextField name="primaryTaxId" control={control} />
            </FieldRow>
            <FieldRow title="Secondary tax ID (e.g. UTR)" centerTitle>
              <TextField name="secondaryTaxId" control={control} />
            </FieldRow>
          </>
        ) : (
          <FieldRow title="Registered tax number (if applicable)" centerTitle>
            <TextField name="registeredTaxNumber" control={control} />
          </FieldRow>
        )}
        <FieldRow title={taxResidenciesLabel} centerTitle>
          {fields.map((field, i) => (
            <React.Fragment key={field.id}>
              <Row centered spacing="xs">
                <Controller
                  name={`taxResidencies.${i}.country`}
                  control={control}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <>
                      <AppSelectBox
                        {...rest}
                        data={nationality}
                        valueKey="Country"
                        labelKey="Country"
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                      />
                      <IconButton
                        onClick={() => append({ country: '' })}
                        size="large"
                      >
                        <AddOutlined />
                      </IconButton>
                      <IconButton
                        disabled={fields.length === 1}
                        onClick={() => (fields.length > 1 ? remove(i) : null)}
                        size="large"
                      >
                        <Remove />
                      </IconButton>
                    </>
                  )}
                />
              </Row>
            </React.Fragment>
          ))}
        </FieldRow>

        <FieldRow title="US person?" centerTitle>
          <Controller
            name="isUsPerson"
            control={control}
            render={({
              field: { ref, value, ...rest },
              fieldState: { error },
            }) => (
              <AppCheckbox
                {...rest}
                label="Yes"
                key="isUsPerson"
                checked={!!value}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FieldRow>
      </Spacing>
    </PageSection>
  );
};
export default TaxAndBankDetail;
